import type { ReactNode } from "react";

/** One card of the panel: a titled surface block. Local to the panel because
 *  the `Card` primitive pads by 24px and carries a shadow, which stacks badly
 *  seven deep inside a drawer. */
export function PanelSection({
  title,
  meta,
  children,
  testId,
}: {
  title: ReactNode;
  /** Right of the title: a count, a caption. */
  meta?: ReactNode;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <section data-testid={testId} className="rounded-lg border border-border bg-surface p-4">
      <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 className="text-h3 text-text">{title}</h3>
        {meta && <span className="text-caption text-text-muted">{meta}</span>}
      </div>
      {children}
    </section>
  );
}

/** The empty/absent state line inside a section. */
export function EmptyLine({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <p data-testid={testId} className="text-body text-text-secondary">
      {children}
    </p>
  );
}

/** The grey line that joins a timeline's icons (the prototype's 2px rail in
 *  the border token). One per item except the last, from the bottom of its
 *  32px icon (`top-8`) through the list's 16px gap (`-bottom-4`) to the top of
 *  the next icon — so it spans exactly the items on screen, page by page.
 *  The item must be `relative`; the icon's centre is 16px in (`left-4`). */
export function TimelineLine() {
  return (
    <span
      aria-hidden="true"
      data-testid="person-timeline-line"
      className="absolute -bottom-4 left-4 top-8 w-0.5 -translate-x-1/2 bg-border"
    />
  );
}
